import { ChevronLeft, Delete, Description, FileDownload } from '@mui/icons-material';
import { CircularProgress, IconButton, Tooltip, Typography, Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, TableFooter } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import { cloneDeep } from 'lodash';
import React, { useEffect, useState } from 'react';
import Api from '../../Api';
import { IDocument } from '../../interfaces/Common/IDocument';
import { IDocumentDialogProps } from '../../interfaces/Common/IDocumentDialogProps';

export const StorageRequestDocumentDialog: React.SFC<IDocumentDialogProps> = ({
    entityId,
    documents,
    onChanged,
    readonly,
    ...props
}: IDocumentDialogProps) => {

    useEffect(() => {
        setDocumentList(documents);
    }, [documents])

    const onChangeFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files && entityId) {
            setUploading(true);

            let formData = new FormData();
            formData.append("storageRequestId", entityId);

            let fileArr = Array.from(e.target.files);
            for (let i = 0; i < fileArr.length; i++) {
                formData.append(fileArr[i].name, fileArr[i]);
            }

            Api.StorageRequests.uploadDocument(entityId, formData).then((data: any) => {
                let documentData = data as IDocument[];
                if (documentData) {
                    setDocumentList(documentData);
                    onChanged(documentData);
                    setUploading(false);
                    e.target.value = "";
                }
            });
        }
    }

    const onDeleteDocument = (documentId: string) => {
        if (entityId) {
            Api.StorageRequests.removeDocument(entityId, documentId).then(() => {
                let newDocuments = cloneDeep(documentList).filter(x => x.documentId !== documentId);
                setDocumentList(newDocuments);
                onChanged(newDocuments);
            });
        }
    }

    const onDownloadDocument = async (d: IDocument) => {
        try {
            const response = await fetch(d.src, {
                method: 'GET',
                headers: {
                    'Content-Type': d.contentType,
                },
            });

            if (!response.ok) {
                throw new Error('Failed to download document');
            }

            const blob = await response.blob();
            const url = window.URL.createObjectURL(blob);

            const link = document.createElement('a');
            link.href = url;
            link.download = d.title;
            document.body.appendChild(link);
            link.click();

            link.parentNode?.removeChild(link);
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error('Download failed:', error);
        }
    };

    const onClose = () => {
        setOpen(false);
    }

    const [open, setOpen] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [documentList, setDocumentList] = useState<IDocument[]>([]);
    const [errorMessage, setErrorMessage] = useState<string | null>(null);

    return (
        <span>
            <Tooltip title="Storage Request Documents">
                <IconButton onClick={() => setOpen(true)}>
                    {documentList !== null && documentList.length > 0 ? (
                        <Description color="primary" />
                    ) : (
                        <Description />
                    )}
                </IconButton>
            </Tooltip>

            <Dialog
                open={open}
                onClose={() => setOpen(false)}
                fullWidth
                maxWidth="sm"
            >
                <form
                    onSubmit={(e) => { }}
                    onInvalid={() => setErrorMessage("please check that all required fields have been populated")}
                >
                    <DialogTitle>Storage Request Documents</DialogTitle>
                    <DialogContent>
                        {documentList !== null && documentList.length > 0 ? (
                            <TableContainer component={Paper}>
                                <Table sx={{ mb: 0 }}>


                                    <TableBody>
                                        {documentList.map((document) => (
                                            <TableRow key={document.documentId}>
                                                <TableCell>{document.title}</TableCell>
                                                <TableCell align="center">
                                                    <IconButton onClick={() => onDownloadDocument(document)}>
                                                        <FileDownload />
                                                    </IconButton>
                                                    {!readonly && (
                                                        <IconButton color="error" onClick={() => onDeleteDocument(document.documentId)}>
                                                            <Delete />
                                                        </IconButton>
                                                    )}
                                                </TableCell>
                                            </TableRow>
                                        ))}

                                        {uploading && (
                                            <TableRow>
                                                <TableCell colSpan={2} align="center">
                                                    <CircularProgress size={40} />
                                                </TableCell>
                                            </TableRow>
                                        )}
                                    </TableBody>

                                    {!readonly && (
                                        <TableFooter>
                                            <UploadButton />
                                        </TableFooter>

                                    )}
                                </Table>
                            </TableContainer>
                        ) : (
                            <Typography sx={{ mb: 2 }} color="GrayText">
                                There are no documents for this storage request.
                                <UploadButton />
                            </Typography>
                        )}



                        {errorMessage && (
                            <DialogContentText color="error">
                                {errorMessage}
                            </DialogContentText>
                        )}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={onClose}>Close</Button>
                    </DialogActions>
                </form>
            </Dialog>
        </span>
    );

    function UploadButton() {
        return <Button
            variant={documentList !== null && documentList.length > 0 ? "outlined" : "contained"}
            component="label"
            disabled={uploading}
            sx={{ m: 2 }}
        >
            Upload
            <input
                hidden
                accept="application/pdf, image/png, image/jpeg"
                multiple
                onChange={onChangeFile}
                type="file" />
        </Button>;
    }
};
